import { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session timeout: 5 minutes (300,000 milliseconds)
  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour (changed from 5 minutes)
  let inactivityTimer = null;

  // Function to reset the inactivity timer
  const resetInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // Only set timer if user is authenticated
    if (token) {
      // Update last activity time in localStorage
      localStorage.setItem("lastActivity", Date.now().toString());

      // Set new timer for session timeout
      inactivityTimer = setTimeout(() => {
        alert("Your session has expired due to inactivity. Please login again.");
        logout();
      }, SESSION_TIMEOUT);
    }
  };

  // Function to check and handle user activity
  const handleUserActivity = () => {
    if (token) {
      const lastActivity = localStorage.getItem("lastActivity");
      const currentTime = Date.now();

      if (lastActivity) {
        const timeSinceLastActivity = currentTime - parseInt(lastActivity);
        
        // If more than session timeout has passed, logout
        if (timeSinceLastActivity >= SESSION_TIMEOUT) {
          alert("Your session has expired due to inactivity. Please login again.");
          logout();
          return;
        }
      }

      // Reset the timer on any activity
      resetInactivityTimer();
    }
  };

  useEffect(() => {
    const initializeAuth = () => {
      const storedToken = localStorage.getItem("accessToken");
      const storedUserId = localStorage.getItem("userId");
      const storedUserName = localStorage.getItem("userName");
      const storedUserEmail = localStorage.getItem("userEmail");
      const lastActivity = localStorage.getItem("lastActivity");

      if (storedToken && storedUserId && lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);

        if (timeSinceLastActivity < SESSION_TIMEOUT) {
          setToken(storedToken);
          setUser({
            userId: Number(storedUserId),
            name: storedUserName,
            email: storedUserEmail,
          });
          
          // Start the inactivity timer
          resetInactivityTimer();
        } else {
          // Session has expired, clean up
          logout();
        }
      }

      setLoading(false);
    };

    initializeAuth();

    // Set up event listeners for user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
      'wheel'
    ];

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // Also listen for API calls (you might want to add this to your axios interceptor)
    const apiActivityHandler = () => {
      handleUserActivity();
    };
    window.addEventListener('api-activity', apiActivityHandler);

    // Cleanup function
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      
      // Remove all event listeners
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('api-activity', apiActivityHandler);
    };
  }, [token]); // Re-run effect when token changes

  const login = (newToken, userData) => {
    localStorage.setItem("accessToken", newToken);
    localStorage.setItem("lastActivity", Date.now().toString());

    if (userData) {
      localStorage.setItem("userId", userData.userId);
      localStorage.setItem(
        "userName",
        `${userData.first_name} ${userData.last_name}`
      );
      localStorage.setItem("userEmail", userData.email);

      setUser({
        userId: Number(userData.userId),
        name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
      });
    }

    setToken(newToken);
    
    // Start the inactivity timer
    resetInactivityTimer();
  };

  const logout = () => {
    // Clear all stored data
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("lastActivity");

    // Clear the inactivity timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};